import { MesfModal } from "@dexteel/mesf-core";
import {
	Box,
	Divider,
	Grid2,
	MenuItem,
	TextField,
	Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { FooterModalButtons } from "@/controls/buttons/FooterModalButtons";
import type { StandData } from "../../models/StandData";
import { useChangePass } from "../../hooks/useChangePass";
import {
	getPassChangeReasons,
	getRollsProduction,
} from "../../repositories/MillSetupRepository";
import { ConfirmationDialog } from "./ConfirmationDialog";

interface ChangePassModalProps {
	stand: StandData;
	productSizeId?: number;
	onClose: () => void;
	onSuccess: () => void;
	onError: (message: string) => void;
}

interface FormValues {
	passPosition: number | null;
	passChangeReasonId: number | null;
	comments: string;
}

const formatTonnage = (val: number | null) =>
	val != null ? val.toFixed(2) : "0.00";

export const ChangePassModal = ({
	stand,
	productSizeId,
	onClose,
	onSuccess,
	onError,
}: ChangePassModalProps) => {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [warningMsg, setWarningMsg] = useState<string | null>(null);
	const warningResolveRef = useRef<((value: boolean) => void) | null>(null);

	const { control, handleSubmit, reset, watch } = useForm<FormValues>({
		defaultValues: {
			passPosition: null,
			passChangeReasonId: null,
			comments: "",
		},
	});

	const selectedPassPosition = watch("passPosition");
	const selectedReasonId = watch("passChangeReasonId");

	// Fetch barrel layout passes
	const { data: barrelPasses = [] } = useQuery({
		queryKey: ["rollsProduction", stand.RollsetId],
		queryFn: () => getRollsProduction(stand.RollsetId!),
		enabled: stand.RollsetId != null,
	});

	// All passes sorted by position, deduplicated (SP may return one row per stand)
	const allPasses = useMemo(() => {
		const seen = new Map<number, (typeof barrelPasses)[0]>();
		for (const p of barrelPasses) {
			if (!seen.has(p.PassPosition)) {
				seen.set(p.PassPosition, p);
			}
		}
		return [...seen.values()].sort((a, b) => a.PassPosition - b.PassPosition);
	}, [barrelPasses]);

	// Fetch pass change reasons
	const { data: reasons = [] } = useQuery({
		queryKey: ["passChangeReasons"],
		queryFn: getPassChangeReasons,
	});

	// Smart default: forward-only search for next good pass (legacy behavior)
	useEffect(() => {
		if (allPasses.length === 0) return;

		const currentPosition = stand.PassPosition ?? 0;

		// Forward-only: find good passes AFTER current position (no wrap-around)
		const goodPassesAfter = allPasses.filter(
			(p) =>
				(p.PassStatusCode === "G" ||
					p.PassStatusCode === "GOOD" ||
					p.PassStatusCode === "A") &&
				p.PassPosition > currentPosition,
		);

		// Prefer passes matching product mill setup pass name
		let nextPass = stand.ProductMillSetupPassName
			? goodPassesAfter.find(
					(p) => p.PassName === stand.ProductMillSetupPassName,
				)
			: null;

		// Fallback: first good pass after current
		if (!nextPass && goodPassesAfter.length > 0) {
			nextPass = goodPassesAfter[0];
		}

		// Final fallback: position 1 (first good pass overall)
		if (!nextPass) {
			const allGood = allPasses.filter(
				(p) =>
					p.PassStatusCode === "G" ||
					p.PassStatusCode === "GOOD" ||
					p.PassStatusCode === "A",
			);
			if (allGood.length > 0) {
				nextPass = allGood[0];
			}
		}

		if (nextPass) {
			reset({
				passPosition: nextPass.PassPosition,
				passChangeReasonId: null,
				comments: "",
			});
		}
	}, [allPasses, stand.PassPosition, stand.ProductMillSetupPassName, reset]);

	// Warning confirmation handlers
	const handleWarning = useCallback((msg: string): Promise<boolean> => {
		return new Promise((resolve) => {
			warningResolveRef.current = resolve;
			setWarningMsg(msg);
		});
	}, []);

	const handleWarningConfirm = useCallback(() => {
		warningResolveRef.current?.(true);
		setWarningMsg(null);
	}, []);

	const handleWarningCancel = useCallback(() => {
		warningResolveRef.current?.(false);
		setWarningMsg(null);
	}, []);

	const { changePass } = useChangePass({
		onSuccess: () => {
			setIsSubmitting(false);
			onSuccess();
		},
		onError: (err) => {
			setIsSubmitting(false);
			onError(err.message);
		},
		onWarning: handleWarning,
	});

	const onSubmit = (data: FormValues) => {
		if (!data.passPosition || !data.passChangeReasonId) return;
		if (!stand.RollsetId) return;

		setIsSubmitting(true);
		changePass({
			standId: stand.Id,
			rollsetId: stand.RollsetId,
			passPosition: data.passPosition,
			passChangeReasonId: data.passChangeReasonId,
			comments: data.comments || null,
			productSizeId,
		});
	};

	// Enter key shortcut
	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (
			e.key === "Enter" &&
			!e.shiftKey &&
			selectedPassPosition &&
			selectedReasonId &&
			!isSubmitting
		) {
			e.preventDefault();
			handleSubmit(onSubmit)();
		}
	};

	return (
		<>
			<MesfModal
				id="modal-change-pass"
				title={`Change pass on stand ${stand.Name}`}
				open={true}
				handleClose={onClose}
				maxWidth="sm"
			>
				<form onSubmit={handleSubmit(onSubmit)} onKeyDown={handleKeyDown}>
					<MesfModal.Content dividers style={{ padding: "15px 30px" }}>
						{/* ── Current Info ── */}
						<Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
							Current
						</Typography>
						<Divider sx={{ mb: 1.5 }} />
						<Box sx={{ pl: 1, mb: 2 }}>
							<Box
								sx={{
									display: "flex",
									py: 0.25,
									borderBottom: "1px solid #e5e5e5",
								}}
							>
								<Typography sx={{ fontSize: 12, color: "#888", minWidth: 110 }}>
									Stand:
								</Typography>
								<Typography sx={{ fontSize: 12, fontWeight: 600 }}>
									{stand.Name}
								</Typography>
							</Box>
							<Box
								sx={{
									display: "flex",
									py: 0.25,
									borderBottom: "1px solid #e5e5e5",
								}}
							>
								<Typography sx={{ fontSize: 12, color: "#888", minWidth: 110 }}>
									Current Pass:
								</Typography>
								<Typography sx={{ fontSize: 12, fontWeight: 600 }}>
									{stand.PassPosition != null && stand.PassPosition > 0
										? `${stand.PassPosition} - ${stand.PassName}`
										: "N/A"}
								</Typography>
							</Box>
						</Box>

						{/* ── New Pass Selection ── */}
						<Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
							New Pass
						</Typography>
						<Divider sx={{ mb: 1.5 }} />

						<Grid2 container spacing={2}>
							<Grid2 size={{ xs: 12 }}>
								<Controller
									name="passPosition"
									control={control}
									rules={{ required: "Select a new pass" }}
									render={({ field, fieldState: { error } }) => (
										<TextField
											select
											label="New Pass"
											value={field.value ?? ""}
											onChange={(e) => field.onChange(Number(e.target.value))}
											fullWidth
											size="small"
											autoFocus
											error={!!error}
											helperText={error?.message}
										>
											{allPasses.map((p) => {
												const isDisabled =
													p.PassStatusCode !== "G" &&
													p.PassStatusCode !== "GOOD" &&
													p.PassStatusCode !== "A";
												return (
													<MenuItem
														key={p.PassPosition}
														value={p.PassPosition}
														disabled={isDisabled}
														title={`Position: ${p.PassPosition} | Name: ${p.PassName} | Scheduled: ${formatTonnage(p.ScheduledTonnage)} tons | Rolled: ${formatTonnage(p.RolledTonnage)} tons | Status: ${isDisabled ? "Not Good (unavailable)" : "Good"}`}
														sx={isDisabled ? { color: "#d5d5d5" } : {}}
													>
														{p.PassPosition} - {p.PassName} -{" "}
														{formatTonnage(p.ScheduledTonnage)}/
														{formatTonnage(p.RolledTonnage)}
													</MenuItem>
												);
											})}
										</TextField>
									)}
								/>
							</Grid2>

							<Grid2 size={{ xs: 12 }}>
								<Controller
									name="passChangeReasonId"
									control={control}
									rules={{ required: "Reason is required" }}
									render={({ field, fieldState: { error } }) => (
										<TextField
											select
											label="Reason for Change"
											value={field.value ?? ""}
											onChange={(e) => field.onChange(Number(e.target.value))}
											fullWidth
											size="small"
											error={!!error}
											helperText={error?.message}
										>
											<MenuItem value="" disabled>
												Choose ...
											</MenuItem>
											{reasons.map((r) => (
												<MenuItem key={r.Id} value={r.Id}>
													{r.Name}
												</MenuItem>
											))}
										</TextField>
									)}
								/>
							</Grid2>

							<Grid2 size={{ xs: 12 }}>
								<Controller
									name="comments"
									control={control}
									render={({ field }) => (
										<TextField
											label="Comments"
											value={field.value}
											onChange={field.onChange}
											fullWidth
											size="small"
											multiline
											rows={3}
											placeholder="Add comments..."
										/>
									)}
								/>
							</Grid2>
						</Grid2>
					</MesfModal.Content>

					<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
						<FooterModalButtons
							isSubmitLoading={isSubmitting}
							onHide={() => onClose()}
							buttonLabel="Accept"
						/>
					</MesfModal.Actions>
				</form>
			</MesfModal>

			<ConfirmationDialog
				open={warningMsg !== null}
				title="Pass change confirmation"
				message={warningMsg || ""}
				onConfirm={handleWarningConfirm}
				onCancel={handleWarningCancel}
			/>
		</>
	);
};
